import { getOperationName } from 'apollo-utilities';
import {
  GetPadConsentFormsForPaymentMethodQuery,
  GetPadConsentFormsForPaymentMethodQueryVariables,
  GetPadConsentFormsQuery,
  GetPadConsentFormsQueryVariables,
  GetPadPaymentMethodConsentQuery,
  GetPadPaymentMethodConsentQueryVariables,
  SavePadPaymentMethodWithConsentMutation,
  SavePadPaymentMethodWithConsentMutationVariables,
  UpdatePadPaymentMethodConsentMutation,
  UpdatePadPaymentMethodConsentMutationVariables,
} from '@/types';
import { gql, useMutation } from '@apollo/client';
import {
  PadPaymentFormActions,
  usePadPaymentFormReducer,
} from './use-pad-payment-form-reducer';
import {
  PAYMENT_CONTEXT_QUERY,
  usePaymentsContext,
} from '@/contexts/payments-context';
import { PAYMENT_METHOD_TYPE_TO_ADYEN_TYPE_MAP } from '@/utils/payments';
import { PaymentMethodType } from '@/enums';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useEffect, useState } from 'react';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useToasts } from '@/contexts/toast-context';

export interface PADConsentFormMarkdown {
  padAdditionalTerms: string;
  padAgreement: string;
}

export const GET_PAD_CONSENT_FORMS_QUERY = gql`
  query GetPadConsentForms(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $accountType: String!
    $consentEmployeeName: String!
    $consentPhoneNumber: String!
    $consentEmail: String!
    $consentStreetAddress: String!
    $consentCity: String!
    $consentProvince: String!
    $consentPostalCode: String!
  ) {
    getPadConsentForms(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      account_type: $accountType
      consent_employee_name: $consentEmployeeName
      consent_phone_number: $consentPhoneNumber
      consent_email: $consentEmail
      consent_street_address: $consentStreetAddress
      consent_city: $consentCity
      consent_province: $consentProvince
      consent_postal_code: $consentPostalCode
    ) {
      alert_level
      alert_message
      pad_additional_terms_and_conditions
      pad_agreement
    }
  }
`;

export const SAVE_PAD_PAYMENT_METHOD_WITH_CONSENT_MUTATION = gql`
  mutation SavePadPaymentMethodWithConsent(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $accountType: String!
    $consentEmployeeName: String!
    $consentPhoneNumber: String!
    $consentEmail: String!
    $consentStreetAddress: String!
    $consentCity: String!
    $consentProvince: String!
    $consentPostalCode: String!
    $paymentMethod: String!
  ) {
    savePadPaymentMethodWithConsent(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      account_type: $accountType
      consent_employee_name: $consentEmployeeName
      consent_phone_number: $consentPhoneNumber
      consent_email: $consentEmail
      consent_street_address: $consentStreetAddress
      consent_city: $consentCity
      consent_province: $consentProvince
      consent_postal_code: $consentPostalCode
      payment_method: $paymentMethod
    ) {
      alert_level
      alert_message
      payment_method_id
    }
  }
`;

export const UPDATE_PAD_PAYMENT_METHOD_CONSENT_MUTATION = gql`
  mutation UpdatePadPaymentMethodConsent(
    $paymentMethodUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $accountType: String!
    $consentEmployeeName: String!
    $consentPhoneNumber: String!
    $consentEmail: String!
    $consentStreetAddress: String!
    $consentCity: String!
    $consentProvince: String!
    $consentPostalCode: String!
  ) {
    updatePadPaymentMethodConsent(
      payment_method_id: $paymentMethodUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      account_type: $accountType
      consent_employee_name: $consentEmployeeName
      consent_phone_number: $consentPhoneNumber
      consent_email: $consentEmail
      consent_street_address: $consentStreetAddress
      consent_city: $consentCity
      consent_province: $consentProvince
      consent_postal_code: $consentPostalCode
    ) {
      alert_level
      alert_message
      payment_method_id
    }
  }
`;

export const GET_PAD_CONSENT_FORMS_FOR_PAYMENT_METHOD_QUERY = gql`
  query GetPadConsentFormsForPaymentMethod(
    $paymentMethodUUID: uuid!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getPadConsentFormsForPaymentMethod(
      payment_method_id: $paymentMethodUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      pad_additional_terms_and_conditions
      pad_agreement
    }
  }
`;

export const GET_PAD_PAYMENT_METHOD_CONSENT_QUERY = gql`
  query GetPadPaymentMethodConsent($paymentMethodUUID: uuid!) {
    pad_payment_method(
      where: { payment_method_uuid: { _eq: $paymentMethodUUID } }
    ) {
      consent_date
      uuid
    }
  }
`;

export const useGetPadConsentForms = () => {
  return useLazyQuery<
    GetPadConsentFormsQuery,
    GetPadConsentFormsQueryVariables
  >(GET_PAD_CONSENT_FORMS_QUERY);
};

export const useSavePadPaymentMethodWithConsent = () => {
  return useMutation<
    SavePadPaymentMethodWithConsentMutation,
    SavePadPaymentMethodWithConsentMutationVariables
  >(SAVE_PAD_PAYMENT_METHOD_WITH_CONSENT_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [getOperationName(PAYMENT_CONTEXT_QUERY) as string],
  });
};

export const useUpdatePadPaymentMethodConsent = () => {
  return useMutation<
    UpdatePadPaymentMethodConsentMutation,
    UpdatePadPaymentMethodConsentMutationVariables
  >(UPDATE_PAD_PAYMENT_METHOD_CONSENT_MUTATION);
};

export const useGetPadConsentFormsForPaymentMethod = () => {
  return useLazyQuery<
    GetPadConsentFormsForPaymentMethodQuery,
    GetPadConsentFormsForPaymentMethodQueryVariables
  >(GET_PAD_CONSENT_FORMS_FOR_PAYMENT_METHOD_QUERY);
};

export const useGetPadPaymentMethodConsent = () => {
  return useLazyQuery<
    GetPadPaymentMethodConsentQuery,
    GetPadPaymentMethodConsentQueryVariables
  >(GET_PAD_PAYMENT_METHOD_CONSENT_QUERY);
};

export const usePadPaymentFlow = () => {
  const { showErrorToast } = useToasts();
  const { refetchPaymentContext, restaurantAddress, selectedPaymentMethod } =
    usePaymentsContext();
  const { user } = useAuthenticatedUserContext();

  const [formState, dispatch] = usePadPaymentFormReducer();
  const [isSubmittingPad, setIsSubmittingPad] = useState(false);
  const [isLoadingConsentForms, setIsLoadingConsentForms] = useState(false);
  const [consentFormsData, setConsentFormsData] =
    useState<PADConsentFormMarkdown | null>(null);

  useEffect(() => {
    if (restaurantAddress) {
      dispatch({
        payload: {
          contactInfo: {
            city: restaurantAddress.city,
            email: user.contact_info.email || '',
            payeeName: user.name,
            phoneNumber: user.contact_info.phone_number,
            postalCode: restaurantAddress.postal_code || '',
            province: restaurantAddress.region || '',
            street:
              restaurantAddress.street_number && restaurantAddress.route
                ? `${restaurantAddress.street_number} ${restaurantAddress.route}`
                : '',
          },
        },
        type: PadPaymentFormActions.HYDRATE,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { selectedRestaurantRelationship, selectedRestaurantUUID } =
    useAuthenticatedUserContext();
  const getPadConsentForms = useGetPadConsentForms();
  const [savePadPaymentMethod] = useSavePadPaymentMethodWithConsent();
  const [updatePadPaymentMethodConsent] = useUpdatePadPaymentMethodConsent();

  const fetchConsentAgreementText = async () => {
    setIsLoadingConsentForms(true);
    try {
      const { contactInfo, padBusinessType } = formState;
      if (padBusinessType) {
        const { data } = await getPadConsentForms({
          variables: {
            accountType: padBusinessType,
            consentCity: contactInfo.city,
            consentEmail: contactInfo.email,
            consentEmployeeName: contactInfo.payeeName,
            consentPhoneNumber: contactInfo.phoneNumber,
            consentPostalCode: contactInfo.postalCode,
            consentProvince: contactInfo.province,
            consentStreetAddress: contactInfo.street,
            restaurantUUID: selectedRestaurantUUID,
            supplierUUID: selectedRestaurantRelationship.supplierUUID,
          },
        });

        const formResponse = data?.getPadConsentForms;
        if (
          formResponse?.pad_additional_terms_and_conditions &&
          formResponse?.pad_agreement
        ) {
          setConsentFormsData({
            padAdditionalTerms:
              formResponse.pad_additional_terms_and_conditions,
            padAgreement: formResponse.pad_agreement,
          });
        }
      }
    } catch {
      showErrorToast();
    } finally {
      setIsLoadingConsentForms(false);
    }
  };

  const handleSubmitPad = async (onSuccess?: () => void) => {
    setIsSubmittingPad(true);
    try {
      const { contactInfo, paymentMethod, padBusinessType } = formState;

      const paymentMethodString = {
        data: {
          paymentMethod: {
            bankAccountNumber: paymentMethod.bankAccountNumber,
            bankCode: paymentMethod.bankCode,
            bankLocationId: paymentMethod.bankLocationId,
            ownerName: paymentMethod.ownerName,
            type: PAYMENT_METHOD_TYPE_TO_ADYEN_TYPE_MAP[PaymentMethodType.PAD],
          },
        },
      };

      await savePadPaymentMethod({
        variables: {
          accountType: padBusinessType!,
          consentCity: contactInfo.city,
          consentEmail: contactInfo.email,
          consentEmployeeName: contactInfo.payeeName,
          consentPhoneNumber: contactInfo.phoneNumber,
          consentPostalCode: contactInfo.postalCode,
          consentProvince: contactInfo.province,
          consentStreetAddress: contactInfo.street,
          paymentMethod: JSON.stringify(paymentMethodString),
          restaurantUUID: selectedRestaurantUUID!,
          supplierUUID: selectedRestaurantRelationship.supplierUUID,
        },
      });

      await refetchPaymentContext();
      onSuccess?.();
    } catch {
      showErrorToast();
    } finally {
      setIsSubmittingPad(false);
    }
  };

  const collectConsentForPaymentMethod = async (onSuccess?: () => void) => {
    setIsSubmittingPad(true);
    try {
      if (selectedPaymentMethod) {
        const { contactInfo, padBusinessType } = formState;
        await updatePadPaymentMethodConsent({
          variables: {
            accountType: padBusinessType!,
            consentCity: contactInfo.city,
            consentEmail: contactInfo.email,
            consentEmployeeName: contactInfo.payeeName,
            consentPhoneNumber: contactInfo.phoneNumber,
            consentPostalCode: contactInfo.postalCode,
            consentProvince: contactInfo.province,
            consentStreetAddress: contactInfo.street,
            paymentMethodUUID: selectedPaymentMethod.uuid,
            restaurantUUID: selectedRestaurantUUID!,
            supplierUUID: selectedRestaurantRelationship.supplierUUID,
          },
        });
      }
      onSuccess?.();
    } catch {
      showErrorToast();
    } finally {
      setIsSubmittingPad(false);
    }
  };

  const isContactInfoValid = Boolean(
    formState.contactInfo.payeeName.trim() &&
      formState.contactInfo.email.trim() &&
      formState.contactInfo.phoneNumber.trim() &&
      formState.contactInfo.street.trim() &&
      formState.contactInfo.city.trim() &&
      formState.contactInfo.province.trim() &&
      formState.contactInfo.postalCode.trim(),
  );

  const isPaymentInfoValid = Boolean(
    formState.paymentMethod.ownerName.trim() &&
      formState.paymentMethod.bankCode.trim() &&
      formState.paymentMethod.bankLocationId.trim() &&
      formState.paymentMethod.bankAccountNumber.trim() &&
      formState.padBusinessType,
  );

  return {
    areAllFieldsValid: isContactInfoValid && isPaymentInfoValid,
    collectConsentForPaymentMethod,
    consentFormsData,
    dispatch,
    fetchConsentAgreementText,
    formState,
    handleSubmitPad,
    isContactInfoValid,
    isLoadingConsentForms,
    isPaymentInfoValid,
    isSubmittingPad,
  };
};
